import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, AlertTriangle, HelpCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";

interface StockItemIntrastatTabProps {
  item: {
    netMassKg?: number;
    countryOfOrigin?: string;
    commodityCode?: string;
    supplementaryUnits?: number;
    supplementaryUnitType?: string;
    applyPlaceOfSupplyVATRules?: boolean;
    itemType?: 'goods' | 'service' | 'digital-service';
    transactionType?: 'b2b' | 'b2c';
    serviceCategory?: string;
  };
  onChange: (field: string, value: any) => void;
  onBlur?: (field: string) => void;
  validationErrors?: { [key: string]: string };
  touchedFields?: Set<string>;
  disabled?: boolean;
}

// Common EU countries for quick selection
const EU_COUNTRIES = [
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "BG", name: "Bulgaria" },
  { code: "HR", name: "Croatia" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" },
  { code: "EE", name: "Estonia" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "GR", name: "Greece" },
  { code: "HU", name: "Hungary" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "LV", name: "Latvia" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MT", name: "Malta" },
  { code: "NL", name: "Netherlands" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "RO", name: "Romania" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "GB", name: "United Kingdom" },
  { code: "CH", name: "Switzerland" },
  { code: "NO", name: "Norway" },
  { code: "IS", name: "Iceland" },
  { code: "LI", name: "Liechtenstein" },
  { code: "TR", name: "Turkey" },
  { code: "US", name: "United States" },
  { code: "CN", name: "China" },
  { code: "JP", name: "Japan" },
  { code: "IN", name: "India" },
  { code: "BR", name: "Brazil" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "MX", name: "Mexico" },
  { code: "KR", name: "South Korea" },
  { code: "ZA", name: "South Africa" },
];

// Common supplementary unit types
const SUPPLEMENTARY_UNIT_TYPES = [
  "Number of items",
  "Pairs",
  "Dozen",
  "Gross",
  "Square metres",
  "Cubic metres",
  "Litres",
  "1000 kWh",
  "Carats",
  "Grams",
  "Kilograms",
  "Tonnes",
  "Metres",
  "Kilometres",
];

// Service categories for digital/professional services
const SERVICE_CATEGORIES = [
  "Software licenses",
  "Cloud services",
  "SaaS subscriptions",
  "Digital content (e-books, music, videos)",
  "Online courses/training",
  "Website hosting",
  "Telecommunications",
  "Broadcasting services",
  "Professional consulting",
  "Design services",
  "Marketing services",
  "IT support services",
  "Other digital services",
];

export function StockItemIntrastatTab({
  item,
  onChange,
  onBlur,
  validationErrors = {},
  touchedFields = new Set(),
  disabled = false,
}: StockItemIntrastatTabProps) {
  const handleFieldChange = (field: string, value: any) => {
    onChange(field, value);
  };

  const handleFieldBlur = (field: string) => {
    if (onBlur) {
      onBlur(field);
    }
  };

  const isService = item.itemType === 'service' || item.itemType === 'digital-service';
  const showVATWarning = item.applyPlaceOfSupplyVATRules && isService;

  return (
    <div className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Intrastat information is required for statistical reporting of goods movements between EU member states.
          This data is used for customs declarations and VAT reporting.
        </AlertDescription>
      </Alert>

      {/* Item Type Selection */}
      <div className="space-y-3">
        <Label>
          Item Type
          <span className="text-muted-foreground text-xs ml-2">
            Select whether this is a physical good or service
          </span>
        </Label>
        <RadioGroup
          value={item.itemType || 'goods'}
          onValueChange={(value) => handleFieldChange('itemType', value)}
          className="flex flex-col space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="goods" id="type-goods" disabled={disabled} />
            <Label 
              htmlFor="type-goods" 
              className={`font-normal ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
            >
              Physical Goods
              <span className="text-muted-foreground text-xs ml-2">
                Tangible products that are shipped
              </span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="service" id="type-service" disabled={disabled} />
            <Label 
              htmlFor="type-service" 
              className={`font-normal ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
            >
              Service
              <span className="text-muted-foreground text-xs ml-2">
                Professional or consulting services
              </span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="digital-service" id="type-digital" disabled={disabled} />
            <Label 
              htmlFor="type-digital" 
              className={`font-normal ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
            >
              Digital Service
              <span className="text-muted-foreground text-xs ml-2">
                Software, SaaS, digital content, telecommunications
              </span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Service Category - Only show for services */}
      {isService && (
        <div className="space-y-2">
          <Label htmlFor="serviceCategory">
            Service Category
            <span className="text-muted-foreground text-xs ml-2">
              Type of service provided
            </span>
          </Label>
          <Select
            value={item.serviceCategory || ""}
            onValueChange={(value) => handleFieldChange("serviceCategory", value)}
            disabled={disabled}
          >
            <SelectTrigger id="serviceCategory">
              <SelectValue placeholder="Select service category" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_CATEGORIES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Transaction Type - Only show for services */}
      {isService && (
        <>
          <Separator />
          <div className="space-y-3">
            <Label>
              Transaction Type
              <span className="text-muted-foreground text-xs ml-2">
                Business-to-Business or Business-to-Consumer
              </span>
            </Label>
            <RadioGroup
              value={item.transactionType || 'b2c'}
              onValueChange={(value) => handleFieldChange('transactionType', value)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="b2b" id="trans-b2b" disabled={disabled} />
                <Label 
                  htmlFor="trans-b2b" 
                  className={`font-normal ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                >
                  B2B (Business to Business)
                  <span className="text-muted-foreground text-xs ml-2">
                    Customer has valid VAT number - Reverse charge applies
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="b2c" id="trans-b2c" disabled={disabled} />
                <Label 
                  htmlFor="trans-b2c" 
                  className={`font-normal ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                >
                  B2C (Business to Consumer)
                  <span className="text-muted-foreground text-xs ml-2">
                    Selling to individuals - Place of supply rules may apply
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* B2B Info Alert */}
          {item.transactionType === 'b2b' && (
            <Alert>
              <HelpCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>B2B Reverse Charge:</strong> When selling to businesses with valid VAT numbers,
                the customer accounts for VAT in their own country. You charge 0% VAT and the customer
                self-assesses the VAT. Ensure you collect and verify the customer's VAT number.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      <Separator />

      {/* Net Mass */}
      <div className="space-y-2">
        <Label htmlFor="netMassKg">
          Net Mass (kg)
          <span className="text-muted-foreground text-xs ml-2">
            Weight of goods excluding packaging
          </span>
        </Label>
        <Input
          id="netMassKg"
          type="number"
          step="0.01"
          min="0"
          placeholder="e.g., 1.2"
          value={item.netMassKg || ""}
          onChange={(e) => handleFieldChange("netMassKg", e.target.value ? parseFloat(e.target.value) : undefined)}
          onBlur={() => handleFieldBlur("netMassKg")}
          disabled={disabled}
          className={
            validationErrors.netMassKg && touchedFields.has("netMassKg")
              ? "border-destructive"
              : ""
          }
        />
        {validationErrors.netMassKg && touchedFields.has("netMassKg") && (
          <p className="text-sm text-destructive">{validationErrors.netMassKg}</p>
        )}
      </div>

      {/* Country of Origin */}
      <div className="space-y-2">
        <Label htmlFor="countryOfOrigin">
          Country of Origin
          <span className="text-muted-foreground text-xs ml-2">
            ISO 2-letter country code
          </span>
        </Label>
        <Select
          value={item.countryOfOrigin || ""}
          onValueChange={(value) => handleFieldChange("countryOfOrigin", value)}
          disabled={disabled}
        >
          <SelectTrigger
            id="countryOfOrigin"
            className={
              validationErrors.countryOfOrigin && touchedFields.has("countryOfOrigin")
                ? "border-destructive"
                : ""
            }
            onBlur={() => handleFieldBlur("countryOfOrigin")}
          >
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {EU_COUNTRIES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                {country.code} - {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {validationErrors.countryOfOrigin && touchedFields.has("countryOfOrigin") && (
          <p className="text-sm text-destructive">{validationErrors.countryOfOrigin}</p>
        )}
      </div>

      {/* Commodity Code */}
      <div className="space-y-2">
        <Label htmlFor="commodityCode">
          Commodity Code
          <span className="text-muted-foreground text-xs ml-2">
            8-digit HS code (e.g., 12345678)
          </span>
        </Label>
        <Input
          id="commodityCode"
          type="text"
          placeholder="e.g., 12345678"
          maxLength={8}
          value={item.commodityCode || ""}
          onChange={(e) => {
            // Only allow digits
            const value = e.target.value.replace(/\D/g, "");
            handleFieldChange("commodityCode", value);
          }}
          onBlur={() => handleFieldBlur("commodityCode")}
          disabled={disabled}
          className={
            validationErrors.commodityCode && touchedFields.has("commodityCode")
              ? "border-destructive"
              : ""
          }
        />
        {validationErrors.commodityCode && touchedFields.has("commodityCode") && (
          <p className="text-sm text-destructive">{validationErrors.commodityCode}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Find commodity codes at{" "}
          <a
            href="https://www.trade-tariff.service.gov.uk/find_commodity"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            UK Trade Tariff
          </a>
        </p>
      </div>

      {/* Supplementary Units */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="supplementaryUnits">
            Supplementary Units
            <span className="text-muted-foreground text-xs ml-2">
              Additional quantity measure
            </span>
          </Label>
          <Input
            id="supplementaryUnits"
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g., 0.00"
            value={item.supplementaryUnits || ""}
            onChange={(e) => handleFieldChange("supplementaryUnits", e.target.value ? parseFloat(e.target.value) : undefined)}
            onBlur={() => handleFieldBlur("supplementaryUnits")}
            disabled={disabled}
            className={
              validationErrors.supplementaryUnits && touchedFields.has("supplementaryUnits")
                ? "border-destructive"
                : ""
            }
          />
          {validationErrors.supplementaryUnits && touchedFields.has("supplementaryUnits") && (
            <p className="text-sm text-destructive">{validationErrors.supplementaryUnits}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="supplementaryUnitType">
            Type
            <span className="text-muted-foreground text-xs ml-2">
              Unit of measure
            </span>
          </Label>
          <Select
            value={item.supplementaryUnitType || ""}
            onValueChange={(value) => handleFieldChange("supplementaryUnitType", value)}
            disabled={disabled}
          >
            <SelectTrigger
              id="supplementaryUnitType"
              className={
                validationErrors.supplementaryUnitType && touchedFields.has("supplementaryUnitType")
                  ? "border-destructive"
                  : ""
              }
              onBlur={() => handleFieldBlur("supplementaryUnitType")}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {SUPPLEMENTARY_UNIT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {validationErrors.supplementaryUnitType && touchedFields.has("supplementaryUnitType") && (
            <p className="text-sm text-destructive">{validationErrors.supplementaryUnitType}</p>
          )}
        </div>
      </div>

      {/* Apply Place of Supply VAT Rules */}
      <div className="space-y-3">
        <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg bg-muted/30">
          <div className="space-y-0.5 flex-1">
            <Label htmlFor="applyPlaceOfSupplyVATRules" className="cursor-pointer flex items-center gap-2">
              Apply Place of Supply VAT Rules
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </Label>
            <p className="text-sm text-muted-foreground">
              For services, apply VAT based on customer's location rather than supplier's location
            </p>
          </div>
          <Switch
            id="applyPlaceOfSupplyVATRules"
            checked={item.applyPlaceOfSupplyVATRules || false}
            onCheckedChange={(checked) => handleFieldChange("applyPlaceOfSupplyVATRules", checked)}
            disabled={disabled || !isService}
          />
        </div>

        {/* Explanation when toggle is OFF */}
        {!item.applyPlaceOfSupplyVATRules && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Standard VAT Rules (Supplier Location)</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>VAT is charged based on <strong>your location</strong> (the supplier).</p>
              <div className="text-sm space-y-1">
                <p><strong>Example:</strong> You're in the UK</p>
                <ul className="list-disc list-inside ml-2">
                  <li>Customer in France → Charge UK VAT (20%)</li>
                  <li>Customer in Germany → Charge UK VAT (20%)</li>
                  <li>You remit all VAT to UK tax authorities</li>
                </ul>
              </div>
              <p className="text-xs mt-2">
                <strong>Use this for:</strong> Physical goods, B2B services with reverse charge
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Warning when toggle is ON */}
        {item.applyPlaceOfSupplyVATRules && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Place of Supply VAT Rules Active</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>VAT is charged based on <strong>customer's location</strong>.</p>
              <div className="text-sm space-y-1">
                <p><strong>Example:</strong> You're in the UK</p>
                <ul className="list-disc list-inside ml-2">
                  <li>Customer in France → Charge French VAT (20%)</li>
                  <li>Customer in Germany → Charge German VAT (19%)</li>
                  <li>Customer in Spain → Charge Spanish VAT (21%)</li>
                </ul>
              </div>
              <div className="mt-3 p-3 bg-background rounded border space-y-2">
                <p className="font-semibold text-sm">⚠️ Important Requirements:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li><strong>OSS Registration:</strong> You must register for the One Stop Shop (OSS) system</li>
                  <li><strong>Customer Location:</strong> You must verify customer's location (2 pieces of evidence)</li>
                  <li><strong>VAT Rates:</strong> You must track and apply correct VAT rate for each EU country</li>
                  <li><strong>Quarterly Returns:</strong> Submit OSS returns every quarter</li>
                  <li><strong>Record Keeping:</strong> Maintain detailed records for 10 years</li>
                </ul>
              </div>
              <p className="text-xs mt-2">
                <strong>Use this for:</strong> Digital services (software, e-books, SaaS), telecommunications, broadcasting, B2C services
              </p>
              <p className="text-xs mt-2">
                <strong>Learn more:</strong>{" "}
                <a
                  href="https://www.gov.uk/guidance/the-vat-rules-if-you-supply-digital-services-to-private-consumers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  UK Government VAT Guide
                </a>
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Warning if enabled for physical goods */}
        {item.applyPlaceOfSupplyVATRules && item.itemType === 'goods' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning: Incorrect Configuration</AlertTitle>
            <AlertDescription>
              Place of Supply VAT Rules should <strong>not</strong> be enabled for physical goods.
              This setting is only for services and digital products. Physical goods follow different VAT rules
              based on shipping and import/export regulations.
            </AlertDescription>
          </Alert>
        )}

        {/* Info if B2B transaction */}
        {isService && item.transactionType === 'b2b' && item.applyPlaceOfSupplyVATRules && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>B2B Transactions</AlertTitle>
            <AlertDescription>
              For B2B transactions, the <strong>reverse charge mechanism</strong> typically applies instead of
              place of supply rules. The customer (if they have a valid VAT number) will account for VAT
              themselves. You should charge 0% VAT and the customer self-assesses.
              <p className="mt-2 text-xs">
                Consider disabling this toggle for B2B transactions and using reverse charge instead.
              </p>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Additional Information */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="space-y-2">
          <p className="font-medium">Intrastat Reporting Requirements:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Required for goods movements between EU member states exceeding threshold values</li>
            <li>Net mass must be reported in kilograms</li>
            <li>Commodity codes follow the Combined Nomenclature (CN) classification</li>
            <li>Some commodities require supplementary units (e.g., number of items, pairs)</li>
            <li>Country of origin determines preferential tariff treatment</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
